import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUp } from "lucide-react";

// Progress ring geometry (in the 48x48 SVG viewBox).
const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function ScrollToTop() {
  const location = useLocation();
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  // Scroll only when the page path changes, not when query params change.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  // Show the button after 300px and track how far down the page we are.
  useEffect(() => {
    const handleScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setVisible(window.scrollY > 300);
      setProgress(scrollable > 0 ? Math.min(window.scrollY / scrollable, 1) : 0);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label={t("scrollToTop")}
      title={t("scrollToTop")}
      tabIndex={visible ? 0 : -1}
      className={`
        group fixed end-6 bottom-[calc(1.5rem+var(--safe-area-bottom))] z-30 [html.has-bottom-nav_&]:max-lg:bottom-[calc(5rem+var(--safe-area-bottom))]
        flex h-12 w-12 items-center justify-center rounded-full
        bg-white/90 text-(--primary-green) shadow-lg shadow-black/10 ring-1 ring-black/5 backdrop-blur
        transition-all duration-300 ease-out
        hover:-translate-y-0.5 hover:bg-(--primary-green) hover:text-white hover:shadow-xl
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40
        dark:bg-slate-800/90 dark:text-emerald-400 dark:ring-white/10 dark:hover:bg-(--primary-green) dark:hover:text-white
        ${visible ? "translate-y-0 scale-100 opacity-100" : "pointer-events-none translate-y-3 scale-90 opacity-0"}
      `}
    >
      {/* Ring fills as the page is scrolled. */}
      <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 48 48" aria-hidden="true">
        <circle cx="24" cy="24" r={RADIUS} fill="none" stroke="currentColor" strokeWidth="2" className="opacity-15" />
        <circle
          cx="24"
          cy="24"
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
        />
      </svg>
      <ArrowUp size={20} strokeWidth={2.25} className="relative transition-transform duration-300 group-hover:-translate-y-0.5" />
    </button>
  );
}
